import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, FormProvider, useFormState } from 'react-hook-form';
import { Grid } from '@material-ui/core';
import RMSCommonDialog from '../../../shared/components/dialog/RMSCommonDialog';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import { RMSSelect } from '../../../shared/components/form-controls';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import {
  ATTRIBUTE_CATEGORIES,
  MULTIPLE_DEFAULT_VALUES,
  DICT_SELECT_PROPS,
} from '../../constants/personFeatureFormConstants';
import useUserDictionaryOptions from '../../hooks/useUserDictionaryOptions';
import { createPersonFeature } from '../../services/userService';

const PersonMultipleFeatureDialog = ({ open, onClose, personId, onSaved }) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.user.userData.user);
  const [submitting, setSubmitting] = useState(false);

  const methods = useForm({
    mode: 'onSubmit',
    defaultValues: MULTIPLE_DEFAULT_VALUES,
  });

  const { isDirty } = useFormState({ control: methods.control });
  const { dictionary } = useUserDictionaryOptions();
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();

  const categoryOptionsMap = useMemo(
    () =>
      ATTRIBUTE_CATEGORIES.reduce(
        (map, category) => ({ ...map, [category]: dictionary?.[category] ?? [] }),
        {}
      ),
    [dictionary]
  );

  const onSubmit = useCallback(
    async (data) => {
      const categoriesToSave = ATTRIBUTE_CATEGORIES.filter((category) => data[category]);

      await executeApiAction({
        setLoading: setSubmitting,
        dispatch,
        action: async () => {
          for (const category of categoriesToSave) {
            await createPersonFeature({
              ptsPersonID: personId,
              AttributeCategory: category,
              Attribute: data[category],
              CreatedBy: currentUser?.Username,
              UpdatedBy: currentUser?.Username,
            });
          }
        },
        onSuccess: () => {
          dispatch(notify('Features created successfully', 'success'));
          onSaved?.();
        },
      });
    },
    [dispatch, personId, currentUser, onSaved]
  );

  const handleSave = methods.handleSubmit(onSubmit);

  const requestClose = useCallback(() => {
    if (isDirty) {
      requestConfirm(
        'You have unsaved changes on these features. Are you sure you want to close without saving?',
        () => onClose?.(),
        'Unsaved Changes'
      );
      return;
    }

    onClose?.();
  }, [isDirty, requestConfirm, onClose]);

  return (
    <RMSCommonDialog
      open={open}
      title="Add Multiple Features"
      onClose={requestClose}
      onSave={handleSave}
      loading={submitting}
      saveDisabled={submitting}
      maxWidth="md"
      fullWidth
      closeOnBackdropClick={false}
      saveButton={{
        show: true,
        type: 'simple',
        label: 'Save',
        variant: 'contained',
        color: 'primary',
      }}
      cancelButton={{
        show: true,
        type: 'simple',
        label: 'Close',
        variant: 'outlined',
        color: 'secondary',
      }}>
      <FormProvider {...methods}>
        <Grid container spacing={2}>
          {ATTRIBUTE_CATEGORIES.map((category) => (
            <Grid item xs={6} key={category}>
              <RMSSelect
                name={category}
                label={category}
                options={categoryOptionsMap[category]}
                {...DICT_SELECT_PROPS}
                showLabelOnly={false}
              />
            </Grid>
          ))}
        </Grid>
      </FormProvider>

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
        />
      )}
    </RMSCommonDialog>
  );
};

export default PersonMultipleFeatureDialog;
